// utils/cvssVector.ts
import type { TagSeverity } from '@praetorian-chariot/ui';

import { CVSS_METRICS } from '@/sections/threats/constants';
import type { ParsedCVSSMetric, ScoreStatus } from '@/sections/threats/types';

/**
 * Parse CVSS vector string into labeled metrics
 */
export function parseCVSSVector(
  vector: string,
  version: string,
  metricGroup: string
): ParsedCVSSMetric[] {
  if (!vector) return [];

  const cleanVector = vector.replace(/^CVSS:[0-9.]+\//, '');

  return cleanVector
    .split('/')
    .map(metric => {
      const [key, value] = metric.split(':');
      const versionMetrics =
        CVSS_METRICS[Number(version)] || CVSS_METRICS[version];
      const metricInfo = versionMetrics?.[metricGroup]?.[key];

      if (!metricInfo) return null;

      return {
        name: metricInfo.name,
        key,
        value,
        label: metricInfo.data[value] || value,
      };
    })
    .filter((metric): metric is ParsedCVSSMetric => metric !== null);
}

/**
 * Get severity status and styling for a score value
 */
export function getStatusForScore(
  score: number,
  normalizeNumber = 1
): ScoreStatus {
  if (score >= 9.0 / normalizeNumber) {
    return {
      status: 'critical' as TagSeverity,
      lineClassName: 'fill-chariot-stroke-critical',
      tagClassName: 'border-chariot-stroke-critical bg-chariot-status-critical',
      progressColor: 'text-chariot-stroke-critical bg-chariot-status-critical',
    };
  }
  if (score >= 7.0 / normalizeNumber) {
    return {
      status: 'high' as TagSeverity,
      lineClassName: 'fill-chariot-stroke-high',
      tagClassName: 'border-chariot-stroke-high bg-chariot-status-high',
      progressColor: 'text-chariot-stroke-high bg-chariot-status-high',
    };
  }
  if (score >= 4.0 / normalizeNumber) {
    return {
      status: 'medium' as TagSeverity,
      lineClassName: 'fill-chariot-stroke-medium',
      tagClassName: 'border-chariot-stroke-medium bg-chariot-status-medium',
      progressColor: 'text-chariot-stroke-medium bg-chariot-status-medium',
    };
  }
  return {
    status: 'low' as TagSeverity,
    lineClassName: 'fill-chariot-stroke-info',
    tagClassName: 'border-chariot-stroke-info bg-chariot-status-info',
    progressColor: 'text-chariot-stroke-info bg-chariot-status-info',
  };
}
